/**
 * First-run hand-off of the Library (W2-10 projects-data; DESIGN.md §4.2).
 *
 * On the first launch the Library asks the first-run question (`DIALOG.firstRun`, W2-14) once and applies the
 * answer. D§4.2: the answer sets language, experience level, keymap preset, hint-bar style and tour:
 *  - "I'm new to charting": Guided level, Default keys, sentence hints, the beginner tour;
 *  - "I've used RPE": Full level, RPE keys and the RPE editing conventions (immediate paste, `12.1` = 12 + 1/snap,
 *    raw RPE units), compact key hints, no beginner tour;
 *  - "I've used other editors": Full level, Default keys and conventions, compact key hints, no beginner tour.
 * First-use tips stay on for everyone. The mapping is the settings service's (`SettingsService.applyFirstRun`,
 * CR-W2-10-4); dismissing the question keeps the defaults (Guided).
 */

import { DIALOG, type DialogMap } from '../../app/dialogs/types';
import type { AppServices } from '../../app/registry/types';

export type FirstRunAnswer = NonNullable<DialogMap['firstRun']['result']>;

/** Writes the answer into the settings (one write) and switches the UI language at once. */
export function applyFirstRun(services: Pick<AppServices, 'settings' | 'i18n'>, answer: FirstRunAnswer): void {
  services.settings.applyFirstRun(answer);
  if (services.i18n.locale !== answer.locale) services.i18n.setLocale(answer.locale);
}

/** Settings objects the question was already asked for in this app start (one question per start). */
const asked = new WeakSet();

/**
 * Asks the first-run question when it has not been answered yet and applies the answer. Resolves with the answer
 * (null when the question was not asked or was dismissed): after "I'm new to charting" the Library points at the
 * next step (UX-076).
 */
export async function firstRunHandOff(services: AppServices): Promise<FirstRunAnswer | null> {
  await services.settings.ready;
  if (services.settings.value.general.firstRunDone) return null;
  if (services.registry.dialog(DIALOG.firstRun) === undefined) return null;
  if (asked.has(services.settings)) return null;
  asked.add(services.settings);
  const answer = await services.ui.openDialog(DIALOG.firstRun, null);
  if (answer !== null) applyFirstRun(services, answer);
  else services.settings.set('general', 'firstRunDone', true);
  return answer;
}
